import {
  Autocomplete,
  Checkbox,
  Chip,
  List,
  ListItem,
  ListItemText,
  Popper,
  TextField,
  Tooltip,
} from "@mui/material";
import { relationshipLevels } from "components/summary/success-plan/constants";
import { useEffect, useRef, useState } from "react";
import { Control, Controller, useController } from "react-hook-form";

type RelationshipControllerProps = {
  control: Control<any>;
  name: string;
};

const RelationshipController = ({ control, name }: RelationshipControllerProps) => {
  const [open, setOpen] = useState(false);

  const { field: otherField, fieldState: otherFieldState } = useController({
    control,
    name: "relationshipOtherContent",
    defaultValue: "",
    rules: {
      maxLength: { value: 20, message: "Maximum 20 characters" },
    },
  });

  // Uncontrolled input ref — typing never triggers re-render
  const otherInputRef = useRef<HTMLInputElement>(null);

  const popperRef = useRef<HTMLDivElement | null>(null);
  const mouseDownInside = useRef(false);

  useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      mouseDownInside.current = popperRef.current?.contains(e.target as Node) ?? false;
    };

    const handleMouseUp = () => {
      mouseDownInside.current = false;
    };

    document.addEventListener("mousedown", handleMouseDown, true);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousedown", handleMouseDown, true);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  const CustomPopper = (props: any) => (
    <Popper
      {...props}
      ref={popperRef}
    />
  );

  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <Autocomplete
          open={open}
          onOpen={() => setOpen(true)}
          PopperComponent={CustomPopper}
          onClose={(_event, reason) => {
            if (reason === "blur" && mouseDownInside.current) {
              return;
            }
            setOpen(false);
          }}
          multiple
          disableCloseOnSelect
          limitTags={1}
          filterOptions={(x) => x}
          isOptionEqualToValue={(o, v) => {
            return o.value.startsWith("Other") && v.value.startsWith("Other")
              ? true
              : o.value === v.value;
          }}
          options={relationshipLevels}
          value={field.value || []}
          onChange={(_, value) => {
            if (value.length > 0 && !value.some((item) => item.value.startsWith("Other"))) {
              otherField.onChange("");
            }
            field.onChange(value);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              variant="outlined"
              placeholder={(field.value || []).length === 0 ? "Select Relationship Level" : ""}
              InputProps={{
                ...params.InputProps,
                style: { flexWrap: "nowrap", height: 36 },
              }}
              sx={{ width: "240px" }}
            />
          )}
          renderTags={(value, getTagProps) => {
            const { key, ...rest } = getTagProps({ index: 0 });
            return (
              <>
                <Chip
                  label={
                    value[0]?.value === "Other" && otherField.value
                      ? `Other - ${otherField.value}`
                      : value[0]?.value || ""
                  }
                  {...rest}
                  key={key}
                  size="small"
                  style={{ maxWidth: 120 }}
                />
                {value.length > 1 && (
                  <Tooltip
                    title={
                      <List
                        disablePadding
                        dense
                      >
                        {value.slice(1).map((item) => (
                          <ListItem
                            dense
                            key={item.value}
                          >
                            <ListItemText
                              primary={
                                item.value === "Other" && otherField.value !== ""
                                  ? `Other - ${otherField.value}`
                                  : item.value
                              }
                            />
                          </ListItem>
                        ))}
                      </List>
                    }
                  >
                    <Chip
                      size="small"
                      label={`${value.length - 1} more`}
                    />
                  </Tooltip>
                )}
              </>
            );
          }}
          renderOption={(props, option, { selected }) => {
            const isOtherSelected = option.value.startsWith("Other") && selected;

            return (
              <li
                {...props}
                key={option.value}
                style={isOtherSelected ? { display: "block" } : undefined}
              >
                <div style={{ display: "flex", alignItems: "center" }}>
                  <Checkbox
                    size="small"
                    checked={selected}
                    disableRipple
                    sx={{ p: 0.5, mr: 0.5 }}
                  />
                  {option.label}
                </div>
                {isOtherSelected && (
                  <div
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <TextField
                      inputRef={otherInputRef}
                      defaultValue={otherField.value || ""}
                      variant="outlined"
                      size="small"
                      fullWidth
                      placeholder="Please specify"
                      autoComplete="off"
                      onBlur={() => {
                        const val = otherInputRef.current?.value || "";
                        otherField.onChange(val);
                        otherField.onBlur();
                        // Close panel if focus moved outside the popper
                        setTimeout(() => {
                          if (!popperRef.current?.contains(document.activeElement)) {
                            setOpen(false);
                          }
                        });
                      }}
                      error={!!otherFieldState.error}
                      helperText={otherFieldState.error?.message}
                      onKeyDown={(e) => e.stopPropagation()}
                      sx={{ mt: 1 }}
                      inputProps={{ maxLength: 20 }}
                    />
                  </div>
                )}
              </li>
            );
          }}
        />
      )}
    />
  );
};

export default RelationshipController;
